import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import MessageTemplatesAPI from '../../api/messageTemplates';

export const state = {
  records: [],
  templateAnalytics: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isSyncing: false,
    isDeleting: false,
    isFetchingAnalytics: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getMessageTemplates: _state => _state.records,
  getTemplateById: _state => id =>
    _state.records.find(t => t.id === Number(id)),
  getTemplatesByStatus: _state => status =>
    _state.records.filter(t => t.status === status),
  getTemplatesByInbox: _state => inboxId =>
    _state.records.filter(t => t.inbox_id === inboxId),
  getCampaignTemplatesByInbox:
    _state =>
    (inboxId, { marketingOnly = true, category = null } = {}) => {
      const normalizedCategory = category?.toString().toLowerCase();
      return _state.records.filter(t => {
        if (t.inbox_id !== inboxId) return false;
        if (t.status !== 'approved') return false;
        if (t.category === 'authentication') return false;
        if (t.name?.startsWith('customer_satisfaction_survey')) return false;

        const components = t.content?.components || [];
        const hasUnsupported = components.some(
          c =>
            ['LIST', 'PRODUCT', 'CATALOG', 'CALL_PERMISSION_REQUEST'].includes(
              c.type
            ) ||
            (c.type === 'HEADER' && c.format === 'LOCATION')
        );
        if (hasUnsupported) return false;

        if (marketingOnly && t.category !== 'marketing') return false;
        if (normalizedCategory && t.category !== normalizedCategory) {
          return false;
        }

        return true;
      });
    },
  getTemplateAnalytics: _state => _state.templateAnalytics,
};

export const actions = {
  get: async ({ commit }, params = {}) => {
    commit(types.SET_MESSAGE_TEMPLATE_UI_FLAG, { isFetching: true });
    try {
      const response = await MessageTemplatesAPI.get(params);
      commit(types.SET_MESSAGE_TEMPLATES, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAG, { isFetching: false });
    }
  },
  create: async ({ commit }, templateObj) => {
    commit(types.SET_MESSAGE_TEMPLATE_UI_FLAG, { isCreating: true });
    try {
      const response = await MessageTemplatesAPI.create(templateObj);
      commit(types.ADD_MESSAGE_TEMPLATE, response.data);
      return response.data;
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_MESSAGE_TEMPLATE_UI_FLAG, { isUpdating: true });
    try {
      const response = await MessageTemplatesAPI.update(id, updateObj);
      commit(types.EDIT_MESSAGE_TEMPLATE, response.data);
      return response.data;
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_MESSAGE_TEMPLATE_UI_FLAG, { isDeleting: true });
    try {
      await MessageTemplatesAPI.delete(id);
      commit(types.DELETE_MESSAGE_TEMPLATE, id);
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAG, { isDeleting: false });
    }
  },
  sync: async ({ commit }, inboxId) => {
    commit(types.SET_MESSAGE_TEMPLATE_UI_FLAG, { isSyncing: true });
    try {
      const response = await MessageTemplatesAPI.sync(inboxId);
      commit(types.SET_MESSAGE_TEMPLATES, response.data);
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAG, { isSyncing: false });
    }
  },
  fetchAnalytics: async ({ commit }, { templateId, days = 30 }) => {
    commit(types.SET_MESSAGE_TEMPLATE_UI_FLAG, { isFetchingAnalytics: true });
    try {
      const response = await MessageTemplatesAPI.analytics(templateId, {
        days,
      });
      commit(types.SET_TEMPLATE_ANALYTICS, response.data.analytics);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_MESSAGE_TEMPLATE_UI_FLAG, {
        isFetchingAnalytics: false,
      });
    }
  },
};

export const mutations = {
  [types.SET_MESSAGE_TEMPLATE_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  [types.SET_MESSAGE_TEMPLATES]: MutationHelpers.set,
  [types.ADD_MESSAGE_TEMPLATE]: MutationHelpers.create,
  [types.EDIT_MESSAGE_TEMPLATE]: MutationHelpers.updateAttributes,
  [types.DELETE_MESSAGE_TEMPLATE]: MutationHelpers.destroy,
  [types.SET_TEMPLATE_ANALYTICS](_state, data) {
    _state.templateAnalytics = data || {};
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
